import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  IconButton,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useNavigate } from "react-router-dom";
import { SeverityIcon } from "./AlertSeverity";
import { useAuth } from "../../contexts/AuthContext";
import {
  getAlertEvents,
  markAlertEventRead,
  markAllAlertEventsRead,
  type AlertEvent,
} from "../../api/alerts";

/**
 * The alert bell in the top bar, backed by `/alerts/events`.
 *
 * Read state lives on the server and is shared — acknowledging an alert
 * acknowledges it for everyone, which is what a control-room board wants.
 */

const REFRESH_MS = 60_000;
const LIMIT = 20;

/** "12s ago" / "3m ago" / "5h ago" / "2d ago" from a seconds count. */
function humanizeAgo(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

type Props = {
  /** Seed events instead of fetching. Used by tests. */
  initial?: AlertEvent[];
};

const NotificationCenter: React.FC<Props> = ({ initial }) => {
  const navigate = useNavigate();
  const { hasAdminAccess } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [events, setEvents] = useState<AlertEvent[]>(initial ?? []);
  const [loading, setLoading] = useState(!initial);
  const [failed, setFailed] = useState(false);
  // Bumped by every local change, so an in-flight fetch that predates it can
  // be discarded instead of overwriting fresher state.
  const mutations = useRef(0);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (initial) return;
      const startedAt = mutations.current;
      try {
        // Current alerts only. Archived ones live on the Alerts page, which is
        // where history — and un-archiving — belongs.
        const fetched = await getAlertEvents(
          { limit: LIMIT, unreadOnly: true },
          signal
        );
        // Opening the menu refreshes, so a response can land after a click has
        // already acknowledged something; that response predates the click, so
        // it is dropped rather than merged. A merge that always preferred
        // "read" would also fight the Alerts page, where an archived alert can
        // be marked unread again.
        if (mutations.current !== startedAt) return;
        setEvents(fetched);
        setFailed(false);
      } catch (e: any) {
        // A dead alerts endpoint must not take the whole top bar down; the
        // bell just stops showing new counts.
        if (e?.name !== "CanceledError") setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [initial]
  );

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    const id = setInterval(() => load(), REFRESH_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  // Everything in the list is current, so the count is simply its length.
  const unread = events.length;
  const open = Boolean(anchorEl);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    load(); // opening is the one moment freshness really matters
  };
  const closeMenu = () => setAnchorEl(null);

  /**
   * Optimistic: acknowledging archives the alert, so it leaves this list at
   * once. It is not gone — the Alerts page keeps it, and can mark it unread.
   */
  const markOneRead = async (n: AlertEvent) => {
    const previous = events;
    mutations.current += 1;
    setEvents((prev) => prev.filter((e) => e.id !== n.id));
    try {
      await markAlertEventRead(n.id);
    } catch {
      setEvents(previous);
      load();
    }
  };

  const markAllRead = async () => {
    const previous = events;
    mutations.current += 1;
    setEvents([]);
    try {
      await markAllAlertEventsRead();
    } catch {
      setEvents(previous);
      load();
    }
  };

  const goToAlerts = () => {
    closeMenu();
    navigate("/alerts");
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton aria-label="notifications" onClick={openMenu} size="large">
          <Badge badgeContent={unread} color="error" overlap="circular">
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        PaperProps={{ sx: { width: 380, maxHeight: 480 } }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle1">Notifications</Typography>
          {/* No Clear here any more: it deleted archived history too, which
              this menu no longer shows. That lives on the Alerts page, behind
              a confirmation. */}
          <Button size="small" onClick={markAllRead} disabled={!unread}>
            Mark all read
          </Button>
        </Box>
        <Divider />

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={22} />
          </Box>
        ) : failed ? (
          <Box p={2}>
            <Typography variant="body2" color="text.secondary">
              Could not load alerts.
            </Typography>
          </Box>
        ) : events.length === 0 ? (
          <Box p={2}>
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          events.map((n) => (
            <MenuItem
              key={n.id}
              onClick={() => markOneRead(n)}
              sx={{
                alignItems: "flex-start",
                py: 1,
                gap: 1,
                whiteSpace: "normal",
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, mt: "2px" }}>
                <SeverityIcon severity={n.severity} />
              </ListItemIcon>
              <ListItemText
                primary={
                  // Every row here is current, so all of them read as unread.
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {n.message}
                  </Typography>
                }
                secondary={
                  <Box
                    component="span"
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    {/* Type labels repeat across areas, so the area is what
                        tells you where this fired. */}
                    <Typography variant="caption" color="text.secondary">
                      {n.area_label} · {n.type_label} · {humanizeAgo(n.age_seconds)}
                    </Typography>
                    {/* Seeded sample rows, until a dispatcher fires real ones. */}
                    {n.is_demo && (
                      <Chip
                        label="Demo"
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    )}
                  </Box>
                }
              />
            </MenuItem>
          ))
        )}

        <Divider />
        {/* No "Manage alerts" link while the Alerts page is parked — it would
            navigate into a page the nav deliberately disables. */}
        <Box p={1} display="flex" justifyContent="flex-end">
          <Button size="small" onClick={closeMenu}>
            Close
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default NotificationCenter;
